import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useSiteActions } from '../../store/actionContext.ts';
import { normalizeStickers } from './presets.js';
import { Sticker } from './Sticker.jsx';
import { StickerLayerRoot, StickerSelectProxy } from './stickerLayer.styled.js';

// Profile sticker overlay. Rendered inside DesignSiteContainer in both Dashboard
// (preview, editable) and Next (live site, read-only). Editing affordances come
// from SiteActionContext: `editStickers` is only true in the Dashboard sticker
// mode, so on Next the layer is purely presentational.
export const StickerLayer = ({ stickers }) => {
  const { editStickers, onUpdateSticker, onDeleteSticker } = useSiteActions();
  const list = normalizeStickers(stickers);

  const rootRef = useRef(null);
  const [layerWidth, setLayerWidth] = useState(0);
  const [selectedId, setSelectedId] = useState(null);

  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const measure = () => setLayerWidth(el.getBoundingClientRect().width);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Click anywhere that isn't a sticker → deselect (only matters in edit mode).
  useEffect(() => {
    if (!editStickers || selectedId == null) return undefined;
    const onDocDown = (e) => {
      // Ignore clicks on a sticker or on a portaled toolbar popup (rendered outside the
      // sticker's DOM subtree, so it wouldn't match [data-sticker-id]).
      if (!e.target.closest || (!e.target.closest('[data-sticker-id]') && !e.target.closest('[data-sticker-popup]'))) setSelectedId(null);
    };
    document.addEventListener('pointerdown', onDocDown, true);
    return () => document.removeEventListener('pointerdown', onDocDown, true);
  }, [editStickers, selectedId]);

  // Re-stack a sticker by adjusting its `z` relative to its siblings (front/back jump to
  // the extremes, forward/backward step by one). Persisted via onUpdateSticker like any field.
  const applyLayer = (id, direction) => {
    const zs = list.map((s) => s.z || 0);
    const curZ = list.find((s) => s.id === id)?.z || 0;
    let nz = curZ;
    if (direction === 'front') nz = Math.max(0, ...zs) + 1;
    else if (direction === 'back') nz = Math.min(0, ...zs) - 1;
    else if (direction === 'forward') nz = curZ + 1;
    else if (direction === 'backward') nz = curZ - 1;
    if (nz !== curZ) onUpdateSticker?.(id, { z: nz });
  };

  if (!list.length && !editStickers) return null;

  const renderSticker = (sticker) => (
    <Sticker
      key={sticker.id}
      sticker={sticker}
      layerWidth={layerWidth}
      editable={!!editStickers}
      selected={!!editStickers && selectedId === sticker.id}
      onSelect={() => setSelectedId(sticker.id)}
      onChange={(patch) => onUpdateSticker?.(sticker.id, patch)}
      onLayer={(direction) => applyLayer(sticker.id, direction)}
      onDelete={() => {
        onDeleteSticker?.(sticker.id);
        setSelectedId(null);
      }}
    />
  );

  // A negative `z` sends a sticker *behind* the page content: those render in a
  // separate overlay painted below the blocks (z-index:-1, contained by the card's
  // `isolation:isolate`), while z >= 0 stay in the front overlay above the blocks.
  // This split is what makes "Send to back" / "Bring to front" cross the content.
  // Exception: the currently-selected sticker always renders in the front overlay
  // so its edit controls (outline, handles, toolbar) stay on top and aren't covered
  // by the blocks while you edit it; it drops behind again once deselected.
  const isFront = (s) => (s.z || 0) >= 0 || (editStickers && selectedId === s.id);
  const behind = list.filter((s) => !isFront(s));
  const front = list.filter(isFront);

  // A behind sticker can sit under an opaque block, which would swallow the click
  // needed to select it. So in edit mode each behind sticker gets a clickable ghost
  // marker in the front overlay (a dashed box at its footprint): it shows where the
  // sticker is and selecting it pops the sticker to the front for editing.
  const renderProxy = (s) => {
    const px = layerWidth ? (layerWidth * s.size) / 100 : 0;
    return (
      <StickerSelectProxy
        key={`proxy-${s.id}`}
        data-sticker-id={s.id}
        title='Sticker sent to back — click to select'
        onPointerDown={(e) => {
          e.stopPropagation();
          setSelectedId(s.id);
        }}
        style={{
          left: `${s.x}%`,
          top: `${s.y}px`,
          width: px ? `${px}px` : `${s.size}%`,
          height: px ? `${px}px` : undefined,
          transform: `translate(-50%, -50%) rotate(${s.rotation}deg)`,
        }}
      />
    );
  };

  return (
    <>
      {behind.length > 0 && (
        <StickerLayerRoot $behind data-sticker-layer='behind'>
          {behind.map(renderSticker)}
        </StickerLayerRoot>
      )}
      <StickerLayerRoot ref={rootRef} data-sticker-layer>
        {front.map(renderSticker)}
        {editStickers && behind.map(renderProxy)}
      </StickerLayerRoot>
    </>
  );
};
